import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'

export const IDLE_MS = 90_000

/** Lock after 90 s without a touch, a click or a key (6.5). The timer restarts on every interaction. */
export function useIdleLock(onIdle: () => void, ms = IDLE_MS, active = true) {
  const cb = useRef(onIdle); cb.current = onIdle
  useEffect(() => {
    if (!active) return
    let t = setTimeout(() => cb.current(), ms)
    const bump = () => { clearTimeout(t); t = setTimeout(() => cb.current(), ms) }
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const
    for (const e of events) window.addEventListener(e, bump, { passive: true })
    return () => { clearTimeout(t); for (const e of events) window.removeEventListener(e, bump) }
  }, [ms, active])
}

/** Who is signed in on this workstation, and the two ways out ("finish", "this is not me"). Shown on every screen of an open session. */
export function KioskBar() {
  const { t } = useTranslation()
  const { kiosk } = useClub()
  useIdleLock(() => void kiosk?.lock('idle'), IDLE_MS, !!kiosk?.session)
  if (!kiosk?.session) return null
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-emerald-950 print:hidden dark:border-night-line dark:bg-night-card dark:text-slate-50" data-testid="kiosk-bar">
      <p className="font-semibold" data-testid="kiosk-who">{t('kiosk.hello', { name: kiosk.session.displayName })}</p>
      <div className="flex gap-2">
        <button className="btn btn-primary !min-h-11 !px-4" onClick={() => void kiosk.lock('finished')} data-testid="kiosk-finish">{t('kiosk.finish')}</button>
        <button className="btn !min-h-11 !px-4" onClick={() => void kiosk.lock('not_me')} data-testid="kiosk-not-me">{t('kiosk.notMe')}</button>
      </div>
    </div>
  )
}
